import { useCallback, useRef } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import type { ScrollView } from 'react-native';

export function useScrollToError<T extends FieldValues>(
  scrollViewRef: React.RefObject<ScrollView>,
  fieldOrder: (keyof T)[],
) {
  const fieldPositions = useRef<Map<keyof T, number>>(new Map());

  const registerField = useCallback((fieldName: keyof T, y: number) => {
    fieldPositions.current.set(fieldName, y);
  }, []);

  const scrollToFirstError = useCallback(
    (errors: FieldErrors<T>) => {
      const errorKeys = Object.keys(errors) as (keyof T)[];
      if (errorKeys.length === 0) return false;

      const firstErrorField = fieldOrder.find((field) =>
        errorKeys.includes(field),
      );

      if (firstErrorField) {
        const y = fieldPositions.current.get(firstErrorField);
        if (y !== undefined) {
          scrollViewRef.current?.scrollTo({
            y: Math.max(0, y - 20),
            animated: true,
          });
          return true;
        }
      }
      return false;
    },
    [scrollViewRef, fieldOrder],
  );

  return { registerField, scrollToFirstError };
}
